import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import addressApi from "api/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import {
  ButtonSize,
  Show,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { Button, ButtonVariant } from "shared/components";
import { Select } from "shared/components/forms/Select";
import { InputSize } from "shared/components/forms/Input";
import { useVerificationTemplatesController } from "views/verification-templates/controller";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import { File02Icon, Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { AddTemplateModal } from "./AddTemplateModal";
import { RemoveTemplateModal } from "./RemoveTemplateModal";
import { Property, PropertyTemplateOption } from "types/landlord/address";
import { Template } from "types/landlord/verificationTemplate";
import { Loader } from "shared/components/Loader";

type ScreeningSettingsVars = {
  verificationTemplateId?: string;
  usePropertyGroupVerificationTemplate?: boolean;
};

type ScreeningSettingsContext = {
  previous?: Property;
};

const getTemplateIdentifier = (template: Template | undefined): string => {
  if (!template) return "";
  return (
    (template as Template & { identifier?: string }).identifier ?? template.id
  );
};

interface ScreeningTemplateSectionProps {
  hasPropertyGroup: boolean;
  verificationTemplateId?: string;
  usePropertyGroupVerificationTemplate: boolean;
  propertyId: number;
  propertyName: string;
  templateOptions?: PropertyTemplateOption[];
}

interface AdditionalTemplate {
  id: number;
  name: string;
  templateIdentifier: string;
}

export const ScreeningTemplateSection = ({
  hasPropertyGroup,
  verificationTemplateId,
  usePropertyGroupVerificationTemplate,
  propertyId,
  propertyName,
  templateOptions
}: ScreeningTemplateSectionProps) => {
  const queryClient = useQueryClient();
  const { templates, isFetching } = useVerificationTemplatesController();
  const [useGroupTemplate, setUseGroupTemplate] = useState(
    usePropertyGroupVerificationTemplate
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    verificationTemplateId
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [templateToRemove, setTemplateToRemove] =
    useState<AdditionalTemplate | null>(null);
  const [additionalTemplates, setAdditionalTemplates] = useState<
    AdditionalTemplate[]
  >([]);
  const [updatingTemplateId, setUpdatingTemplateId] = useState<number | null>(
    null
  );

  // Fetch template_options from the dedicated endpoint so the section is
  // self-contained — independent of whether the parent property GET inlines
  // them (slim default does, progressive does not).
  const templateOptionsQueryKey = useMemo(
    () => ["properties", String(propertyId), "template_options"],
    [propertyId]
  );
  const { data: fetchedTemplateOptions } = useQuery<PropertyTemplateOption[]>({
    queryKey: templateOptionsQueryKey,
    queryFn: () => addressApi.getPropertyTemplateOptions(propertyId),
    // Seed with the inline value from the parent property GET when present,
    // and treat that seed as fresh so we don't fire an extra request just
    // for parity. Mutations invalidate templateOptionsQueryKey to refetch.
    initialData: templateOptions,
    initialDataUpdatedAt: templateOptions ? Date.now() : undefined,
    staleTime: Infinity
  });

  useEffect(() => {
    const source = fetchedTemplateOptions ?? [];
    if (source.length > 0) {
      const mappedTemplates = source.map((option) => ({
        id: option.id,
        name: option.name,
        templateIdentifier: option.verification_template_identifier
      }));
      setAdditionalTemplates(mappedTemplates);
    } else setAdditionalTemplates([]);
  }, [fetchedTemplateOptions]);

  const propertyQueryKey = useMemo(
    () => ["properties", String(propertyId)],
    [propertyId]
  );

  const { mutate: editScreeningSettings, isPending } = useMutation<
    unknown,
    Error,
    ScreeningSettingsVars,
    ScreeningSettingsContext
  >({
    mutationKey: ["PROPERTIES", propertyId, "UPDATE_TEMPLATE"],
    mutationFn: ({
      verificationTemplateId,
      usePropertyGroupVerificationTemplate
    }) => {
      return addressApi.editProperty({
        propertyId,
        verification_template_id: verificationTemplateId,
        use_property_group_verification_template:
          usePropertyGroupVerificationTemplate
      });
    },
    onMutate: async ({
      verificationTemplateId,
      usePropertyGroupVerificationTemplate
    }) => {
      await queryClient.cancelQueries({ queryKey: propertyQueryKey });
      const previous = queryClient.getQueryData<Property>(propertyQueryKey);

      queryClient.setQueryData<Property>(propertyQueryKey, (old) => {
        if (!old) return old;
        const updates: Partial<Property> = {};
        if (verificationTemplateId !== undefined) {
          const next = templates?.find(
            (t) => getTemplateIdentifier(t) === verificationTemplateId
          );
          if (next) updates.verification_template = next;
        }
        if (usePropertyGroupVerificationTemplate !== undefined)
          updates.use_property_group_verification_template =
            usePropertyGroupVerificationTemplate;
        return { ...old, ...updates };
      });

      return { previous };
    },
    onSuccess: (_data, vars) => {
      toast.success("Screening template updated");
      // When the property-group toggle flips, the effective verification_template
      // switches between the property's own and the group's. We don't know the
      // group's template locally, so refetch to get the authoritative value.
      if (vars.usePropertyGroupVerificationTemplate !== undefined)
        queryClient.invalidateQueries({ queryKey: propertyQueryKey });
    },
    onError: (error, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(propertyQueryKey, context.previous);
      toast.error(
        "Failed to update screening template",
        getErrorMessage(error)
      );
    }
  });

  const { mutate: saveTemplateOptions, isPending: isSavingTemplateOptions } =
    useMutation({
      mutationKey: ["PROPERTIES", propertyId, "UPDATE_TEMPLATE_OPTIONS"],
      mutationFn: (templates: AdditionalTemplate[]) => {
        const templateOptionsPayload = templates.map((template) => ({
          ...(template.id !== 0 ? { id: template.id } : {}),
          name: template.name,
          verification_template_identifier: template.templateIdentifier
        }));

        return addressApi.editProperty({
          propertyId,
          template_options: templateOptionsPayload
        });
      },
      onSuccess: () => {
        toast.success("Template options updated");
        queryClient.invalidateQueries({ queryKey: templateOptionsQueryKey });
        queryClient.invalidateQueries({
          queryKey: ["properties", String(propertyId)]
        });
        queryClient.invalidateQueries({
          queryKey: ["UNIT_TEMPLATE_OPTIONS"]
        });
      },
      onError: (error) => {
        toast.error(
          "Failed to update template options",
          getErrorMessage(error)
        );
      }
    });

  const handleUseGroupTemplateChange = ({ checked }: { checked: boolean }) => {
    setUseGroupTemplate(checked);
    editScreeningSettings({
      usePropertyGroupVerificationTemplate: checked
    });
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    editScreeningSettings({
      verificationTemplateId: templateId
    });
  };

  const handleAddAdditionalTemplate = () => {
    setIsModalOpen(true);
  };

  const handleRemoveTemplate = (template: AdditionalTemplate) => {
    setTemplateToRemove(template);
    setIsRemoveModalOpen(true);
  };

  const handleConfirmRemove = () => {
    if (templateToRemove) {
      const previousTemplates = [...additionalTemplates];
      const updatedTemplates = additionalTemplates.filter(
        (t) => t.id !== templateToRemove.id
      );
      setAdditionalTemplates(updatedTemplates);
      saveTemplateOptions(updatedTemplates, {
        onError: (error) => {
          setAdditionalTemplates(previousTemplates);
          toast.error("Failed to remove template", getErrorMessage(error));
        }
      });
      setTemplateToRemove(null);
      setIsRemoveModalOpen(false);
    }
  };

  const handleCancelRemove = () => {
    setTemplateToRemove(null);
    setIsRemoveModalOpen(false);
  };

  const handleSaveNewTemplate = (template: {
    name: string;
    templateIdentifier: string;
  }) => {
    const previousTemplates = [...additionalTemplates];
    const newTemplate: AdditionalTemplate = {
      id: 0,
      name: template.name,
      templateIdentifier: template.templateIdentifier
    };

    const updatedTemplates = [...additionalTemplates, newTemplate];
    setAdditionalTemplates(updatedTemplates);
    saveTemplateOptions(updatedTemplates, {
      onError: (error) => {
        setAdditionalTemplates(previousTemplates);
        toast.error("Failed to add template", getErrorMessage(error));
      }
    });
    setIsModalOpen(false);
  };

  const handleCancelModal = () => {
    setIsModalOpen(false);
  };

  const verificationTemplateOptions = useMemo(() => {
    return (
      templates?.map((template) => ({
        value: getTemplateIdentifier(template),
        label: template.name
      })) || []
    );
  }, [templates]);

  const defaultTemplate = templates?.find((t) => t.default);
  const propertyGroupOption = {
    value: "property-group",
    label: "Property group template"
  };

  const getTemplateSelectValue = () => {
    if (useGroupTemplate) return "property-group";
    if (selectedTemplateId) return selectedTemplateId;
    if (defaultTemplate) return getTemplateIdentifier(defaultTemplate);
    return "";
  };

  if (isFetching)
    return (
      <SectionCard title="Screening template">
        <div className="flex items-center justify-center w-[700px]">
          <Loader />
        </div>
      </SectionCard>
    );

  return (
    <SectionCard title="Screening template">
      <div className="-my-6 -mx-8">
        <div className="border-b border-alpha-40">
          <div className="py-7 px-8 flex items-center gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-20 rounded-lg flex items-center justify-center">
              <HugeiconsIcon
                icon={File02Icon as IconSvgElement}
                className="size-5 text-primary-500"
              />
            </div>
            <h3 className="flex-1 text-base font-medium text-content-1000">
              Default template
            </h3>
            <Show if={hasPropertyGroup}>
              <Switch
                size={SwitchSize.SM}
                wrapperClassName="!gap-2"
                label="Property group template"
                className="text-content-300 text-sm font-medium"
                checked={useGroupTemplate}
                onChange={(checked) =>
                  handleUseGroupTemplateChange({ checked })
                }
                disabled={isPending}
              />
            </Show>
            <div className="h-10 w-px bg-alpha-40" />
            <Select
              id="default-template-select"
              value={getTemplateSelectValue()}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              isDisabled={useGroupTemplate || isPending || isFetching}
              options={
                useGroupTemplate
                  ? [propertyGroupOption]
                  : verificationTemplateOptions
              }
              className="w-60 !mb-0"
              size={InputSize.SM}
            />
          </div>
        </div>

        <Show if={additionalTemplates.length === 0}>
          <div className="p-6">
            <div className="bg-background-900 border border-dashed rounded-lg py-6 flex flex-col items-center justify-center gap-4 relative ">
              <p className="text-sm font-medium text-content-300 text-center max-w-xl">
                If you want to have application links for units in this property
                with screening settings different from the default, you can add
                additional templates
              </p>
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.BASE}
                onClick={handleAddAdditionalTemplate}
                disabled={isPending || isSavingTemplateOptions}
              >
                Add additional template
              </Button>
            </div>
          </div>
        </Show>

        <Show if={additionalTemplates.length > 0}>
          <p className="bg-background-900 px-8 py-2 text-sm font-medium text-content-300">
            Additional templates
          </p>

          <div className="bg-white py-4 border-t border-alpha-40">
            {additionalTemplates.map((template) => (
              <div
                key={template.id}
                className="flex items-center px-8 py-2 h-14"
              >
                <div className="flex-1 flex items-center gap-4">
                  <div className="flex-shrink-0 size-10 border border-alpha-40 rounded-lg flex items-center justify-center">
                    <HugeiconsIcon
                      icon={File02Icon as IconSvgElement}
                      className="size-5 text-content-300"
                    />
                  </div>
                  <p className="text-base font-medium text-content-1000">
                    {template.name}
                  </p>
                </div>
                <div className="flex pl-4 items-center gap-2">
                  <Select
                    id={`additional-template-${template.id}`}
                    value={template.templateIdentifier}
                    onChange={(e) => {
                      if (updatingTemplateId !== null) return;

                      setUpdatingTemplateId(template.id);
                      const previousTemplates = [...additionalTemplates];
                      const updated = additionalTemplates.map((t) =>
                        t.id === template.id
                          ? {
                              ...t,
                              templateIdentifier: e.target.value
                            }
                          : t
                      );
                      setAdditionalTemplates(updated);
                      saveTemplateOptions(updated, {
                        onError: (error) => {
                          setAdditionalTemplates(previousTemplates);
                          toast.error(
                            "Failed to update template",
                            getErrorMessage(error)
                          );
                        },
                        onSettled: () => {
                          setUpdatingTemplateId(null);
                        }
                      });
                    }}
                    isDisabled={
                      isSavingTemplateOptions || updatingTemplateId !== null
                    }
                    options={verificationTemplateOptions}
                    className="w-[193px] h-10 !mb-0"
                    size={InputSize.SM}
                  />
                  <div className="flex-shrink-0 size-10 border border-alpha-100 rounded-lg flex items-center justify-center">
                    <IconButton
                      size={IconButtonSize.SM}
                      variant={IconButtonVariant.TEXT_BASE}
                      HugeIcon={Cancel01Icon}
                      onClick={() => handleRemoveTemplate(template)}
                      disabled={isPending || isSavingTemplateOptions}
                      className="text-content-400 hover:text-content-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-alpha-40 p-6">
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.BASE}
              onClick={handleAddAdditionalTemplate}
              disabled={isPending || isSavingTemplateOptions}
            >
              Add additional templates
            </Button>
          </div>
        </Show>
      </div>

      <AddTemplateModal
        opened={isModalOpen}
        onClose={handleCancelModal}
        onSave={handleSaveNewTemplate}
        existingTemplates={additionalTemplates}
        templateOptions={verificationTemplateOptions}
        isDisabled={isFetching}
        isSaving={isSavingTemplateOptions}
      />

      <RemoveTemplateModal
        opened={isRemoveModalOpen}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        templateName={templateToRemove?.name || ""}
        propertyName={propertyName}
      />
    </SectionCard>
  );
};
